/**
 * Auth storage probe (workorder-auth-drop-ios-diagnostics-2026-11-03, tasks 1, 2 and 4).
 *
 * Watches what the Auth SDK reads from and writes to the stored user BEFORE the SDK acts on it. Background: on iOS
 * ~150 sessions a day lose their signed-in user ~2 s after the start (`auth state dropped after sign-in`,
 * `persistence: local`, `returned: false`). The SDK polls localStorage every second on mobile
 * (`BrowserLocalPersistence.fallbackToPolling`), takes a `null` for the auth key as a logout, and REMOVES the key
 * itself (`directlySetCurrentUser(null)` -> `removeCurrentUser()`) before our auth listener fires - afterwards every
 * measurement only sees "key missing". This probe sits in front of that moment and tells the three candidates apart:
 *   K1 read glitch   - `getItem` returns null (or another string) although the key was there: `nullRead` / `valueChanged`
 *   K2 other context - a real `storage` event from another tab/window of the origin removed the key: `storageEvent`
 *   K3 backend       - the token refresh / accounts:lookup answered TOKEN_EXPIRED etc.: `lastAuthHttpError`
 *
 * Installed from main.ts before the bootstrap (installAuthStorageProbe), so that the SDK's first `getItem` is seen.
 * Framework-free, browser only, every hook in try/catch - a throwing storage or a failing hook disables the probe
 * silently (`active: false`), the original storage calls are never changed. Only keys with the prefix
 * `firebase:authUser:` in localStorage are tracked; sessionStorage and other keys pass through untouched.
 *
 * What is recorded (ring buffer of EVENT_BUFFER_SIZE events, no tokens, no uids, no raw stacks - only lengths,
 * hashes and uid equality): see ProbeEvent. The stack of a read is classified by the SDK method names that survive
 * minification (`forAllChangedKeys` = poll, `onStorageEvent` = event path, `getCurrentUser`/`_get` = the SDK's own
 * re-read) and hashed (djb2 of the frame names, urls and positions stripped).
 *
 * Task 4 - the prepared healing, SHIPPED OFF: with AUTH_STORAGE_PROBE_HEAL = true the `getItem` wrapper returns the
 * shadow value instead of a `null` read while the shadow is set (`healed` counts). Flip the constant only after the 24 h
 * evaluation proved K1 (report §7). A real removal clears the shadow (removeItem, storage event), so a legitimate
 * logout is never healed.
 */

/** task 4: heal a null read of the auth key with the shadow value - off until K1 is proven (report §7) */
export const AUTH_STORAGE_PROBE_HEAL = false;
/** the SDK's key of the stored user: `firebase:authUser:<apiKey>:<appName>` */
export const AUTH_USER_KEY_PREFIX = 'firebase:authUser:';
const EVENT_BUFFER_SIZE = 50;
const SUMMARY_EVENTS = 5;
/** a removeItem this soon after a nullRead is the SDK's logout after the glitch */
const NULL_READ_REMOVE_WINDOW_MS = 50;
/** a removeItem this soon after a real storage event (key removed elsewhere) is the SDK following the other context */
const STORAGE_EVENT_REMOVE_WINDOW_MS = 1500;
/** a removeItem this soon after AuthService.logOut() is our own sign-out */
const APP_SIGN_OUT_WINDOW_MS = 5000;
/** lastAuthHttpError: a failed auth backend call this long before the drop */
const HTTP_ERROR_WINDOW_MS = 10000;
/** keyBeforeDrop: the key was seen (read or written, non-null) this long before the drop - one poll tick plus slack */
const KEY_BEFORE_DROP_WINDOW_MS = 1500;
const AUTH_HOSTS = ['securetoken.googleapis.com', 'identitytoolkit.googleapis.com'];
const TABS_CHANNEL = 'whocan-tabs';
const IN_APP_BROWSERS: ReadonlyArray<[RegExp, string]> = [
    [/WhatsApp/i, 'whatsapp'], [/Instagram/i, 'instagram'], [/FBAN|FBAV/i, 'facebook'], [/\bLine\//i, 'line'],
    [/TikTok|musical_ly|Bytedance/i, 'tiktok'], [/\bGSA\//i, 'google-app'], [/MicroMessenger/i, 'wechat'], [/Snapchat/i, 'snapchat'],
];

export type ProbeEventType = 'nullRead' | 'valueChanged' | 'removed' | 'uidReplaced' | 'storageEvent' | 'healed';
/** who read the key: the SDK's 1 s poll, the SDK's storage-event path, the SDK's own re-read, the app, unknown (no stack) */
export type ReadCaller = 'sdkPoll' | 'sdkEvent' | 'sdkGet' | 'app' | 'unknown';
/**
 * why the key was removed: the SDK right after a null read (K1), after a real storage event (K2), after a failed auth
 * backend call (K3), our own logout, the logout of another tab of the app (announced on the tabs channel), the SDK for a
 * reason not seen, an explicit removal outside the SDK, unknown
 */
export type RemovedBy = 'sdkAfterNullRead' | 'sdkAfterStorageEvent' | 'sdkAfterHttpError' | 'sdkSignOut' | 'peerSignOut' | 'sdk' | 'appExplicit' | 'unknown';

export interface ProbeEvent {
    type: ProbeEventType;
    /** ms since the probe was installed (~ document start) */
    sinceStartMs: number;
    visibility: string;
    /** nullRead: who read, the hashed stack, ms since the last write of the key (null = none) */
    caller?: ReadCaller;
    stackHash?: string;
    msSinceLastWrite?: number | null;
    /**
     * nullRead: the key is still enumerable (`storage.key(i)`) although getItem returned null = a true read glitch (K1);
     * `no` with a storageLen > 0 = the key alone is gone (K2 or the SDK), `no` with storageLen 0 = the whole storage is empty
     */
    keyListed?: 'yes' | 'no' | 'unknown';
    storageLen?: number | null;
    /** valueChanged / storageEvent (with a value) / uidReplaced: the uid of the new value equals the shadow's */
    sameUid?: boolean;
    lenBefore?: number;
    lenAfter?: number;
    /** valueChanged: the access token differs (hash) */
    tokenChanged?: boolean;
    removedBy?: RemovedBy;
    /** storageEvent: the key is gone in the other context (newValue null); `cleared` = storage.clear() (key null) */
    newValueNull?: boolean;
    cleared?: boolean;
    /** storageEvent (newValue null): another tab of the app announced its own logout right before - no anomaly */
    peerSignOut?: boolean;
    /** storageEvent: pathname of the document that changed the key (no query, no hash) */
    url?: string;
}

/** the short form that goes into the drop report and the anomaly line */
export interface ProbeSummary {
    active: boolean;
    nullReads: number;
    valueChanges: number;
    /** class of the last removeItem of the key, `none` without one */
    removedBy: RemovedBy | 'none';
    storageEvents: number;
    lastNullReadSinceStartMs: number | null;
    /** keyListed / storageLen of the FIRST null read of the session (the tick that logged the user out) */
    nullReadKeyListed: 'yes' | 'no' | 'unknown' | null;
    nullReadStorageLen: number | null;
    healed: number;
    last: ProbeEvent[];
}

/** the context of the drop, captured by AuthService at the moment of the user -> null emission (task 2) */
export interface AuthDropContext {
    probe: ProbeSummary;
    /** the key was observed non-null within KEY_BEFORE_DROP_WINDOW_MS before the drop */
    keyBeforeDrop: 'yes' | 'no' | 'unknown';
    /** `<status> <code>` of the last failed securetoken / identitytoolkit call within HTTP_ERROR_WINDOW_MS, else `none` */
    lastAuthHttpError: string;
    navType: string;
    standalone: boolean;
    inApp: string;
    visibility: string;
    hiddenSinceStart: boolean;
    /** other open Whocan tabs of the origin (BroadcastChannel), null when the channel is not available */
    tabs: number | null;
    storageQuotaErr: 'yes' | 'no';
}

export type AnomalyListener = (summary: ProbeSummary, sinceStartMs: number) => void;

interface StorageOriginals {
    getItem: Storage['getItem'];
    setItem: Storage['setItem'];
    removeItem: Storage['removeItem'];
}

function hashText(text: string): string {
    let hash = 5381;
    for (let index = 0; index < text.length; index++) {
        hash = ((hash * 33) ^ text.charCodeAt(index)) >>> 0;
    }
    return hash.toString(16);
}

/** uid and a hash of the access token of a stored user value - never the token itself */
function identityOf(value: string | null): { uid: string | null; tokenHash: string | null } {
    if (!value) return { uid: null, tokenHash: null };
    try {
        const parsed = JSON.parse(value) as { uid?: unknown; stsTokenManager?: { accessToken?: unknown } };
        const token = parsed?.stsTokenManager?.accessToken;
        return { uid: typeof parsed?.uid === 'string' ? parsed.uid : null, tokenHash: typeof token === 'string' ? hashText(token) : null };
    } catch {
        return { uid: null, tokenHash: null };
    }
}

/** the frame names of a stack, urls and positions stripped - what the hash and the classification work on */
function stackFrames(): string {
    try {
        const stack = new Error().stack ?? '';
        return stack.split('\n').slice(0, 12).map(line => line.replace(/\(?(https?|file|blob):[^\s)]+\)?/g, '').replace(/:\d+(:\d+)?/g, '').trim()).join('|');
    } catch {
        return '';
    }
}

function classifyReader(frames: string): ReadCaller {
    if (/forAllChangedKeys|startPolling/.test(frames)) return 'sdkPoll';
    if (/onStorageEvent/.test(frames)) return 'sdkEvent';
    if (/getCurrentUser|PersistenceUserManager|\b_get\b/.test(frames)) return 'sdkGet';
    if (/AuthService|detectAuthPersistence|storageHasAuthUser/.test(frames)) return 'app';
    return frames ? 'unknown' : 'unknown';
}

export class AuthStorageProbe {
    /** the wrappers are in place and bookkeeping runs; false before install, after a failure, after uninstall */
    public active = false;
    public readonly counters = { installs: 0, authInits: 0, authServiceStarts: 0 };
    private readonly heal: boolean;
    private storage: Storage | null = null;
    private originals: StorageOriginals | null = null;
    private startAt = 0;
    private events: ProbeEvent[] = [];
    private nullReads = 0;
    private valueChanges = 0;
    private storageEvents = 0;
    private healed = 0;
    private lastRemovedBy: RemovedBy | null = null;
    private lastNullReadAt: number | null = null;
    private firstNullRead: { keyListed: 'yes' | 'no' | 'unknown'; storageLen: number | null } | null = null;
    private lastStorageEventNullAt: number | null = null;
    private lastWriteAt: number | null = null;
    private appSignOutAt: number | null = null;
    /** when another tab of the app announced its logout on the tabs channel (AuthService.logOut() there) */
    private peerSignOutAt: number | null = null;
    private lastHttpError: { at: number; status: number; code: string } | null = null;
    /** the last non-null value seen (read or written) and its identity; cleared by removeItem / a storage event */
    private shadow: string | null = null;
    private shadowUid: string | null = null;
    private shadowTokenHash: string | null = null;
    /** when the key was last observed non-null - stays after a removal (keyBeforeDrop) */
    private shadowAt: number | null = null;
    private quotaError = false;
    private hiddenSinceStart = false;
    private anomalyAt: number | null = null;
    private anomalyListener: AnomalyListener | null = null;
    private channel: { postMessage: (message: unknown) => void; close: () => void; onmessage: ((event: { data: unknown }) => void) | null } | null = null;
    private readonly tabId = Math.random().toString(36).slice(2);
    private readonly peers = new Set<string>();
    private originalFetch: typeof fetch | null = null;
    private readonly listeners: Array<[string, EventListener]> = [];

    constructor(options: { heal?: boolean } = {}) {
        this.heal = options.heal ?? false;
    }

    /** installs the wrappers once; a second call only counts (a second bootstrap of the same document) */
    install(): void {
        this.counters.installs++;
        if (this.originals !== null) return;
        try {
            if (typeof window === 'undefined' || typeof Storage === 'undefined') return;
            this.storage = window.localStorage; // throws when the storage is blocked -> stays inactive
            this.startAt = this.now();
            this.wrapStorage();
            this.listen('storage', event => this.onStorageEvent(event as StorageEvent));
            this.listen('visibilitychange', () => { if (document.visibilityState === 'hidden') this.hiddenSinceStart = true; });
            this.listen('pagehide', () => { try { this.channel?.postMessage({ t: 'bye', id: this.tabId }); } catch { /* ignore */ } });
            this.hiddenSinceStart = document.visibilityState === 'hidden';
            this.wrapFetch();
            this.openChannel();
            this.active = true;
        } catch {
            this.disable();
        }
    }

    /** restores the originals (tests) and resets the state */
    uninstall(): void {
        try {
            if (this.originals) {
                Storage.prototype.getItem = this.originals.getItem;
                Storage.prototype.setItem = this.originals.setItem;
                Storage.prototype.removeItem = this.originals.removeItem;
            }
            if (this.originalFetch) window.fetch = this.originalFetch;
            for (const [type, listener] of this.listeners) window.removeEventListener(type, listener);
            this.channel?.close();
        } catch { /* ignore */ }
        this.originals = null;
        this.originalFetch = null;
        this.listeners.length = 0;
        this.channel = null;
        this.active = false;
        this.events = [];
        this.nullReads = this.valueChanges = this.storageEvents = this.healed = 0;
        this.lastRemovedBy = this.lastNullReadAt = this.lastStorageEventNullAt = this.lastWriteAt = this.appSignOutAt = this.peerSignOutAt = null;
        this.firstNullRead = null;
        this.lastHttpError = null;
        this.shadow = this.shadowUid = this.shadowTokenHash = null;
        this.shadowAt = null;
        this.quotaError = this.hiddenSinceStart = false;
        this.anomalyAt = null;
        this.anomalyListener = null;
        this.peers.clear();
        this.counters.installs = this.counters.authInits = this.counters.authServiceStarts = 0;
    }

    /** a bootstrap counter for the second-bootstrap check (task 0.4): provideAuth factory, AuthService constructor */
    count(name: 'authInits' | 'authServiceStarts'): void {
        this.counters[name]++;
    }

    /** AuthService.logOut() right before afAuth.signOut(): the following removeItem is ours - the other tabs learn it too */
    markAppSignOut(): void {
        this.appSignOutAt = this.now();
        try { this.channel?.postMessage({ t: 'signOut', id: this.tabId }); } catch { /* ignore */ }
    }

    /** the first anomaly of the session (nullRead, or a storage event that removed the key) - once; a late listener gets it right away */
    onAnomaly(listener: AnomalyListener): void {
        this.anomalyListener = listener;
        if (this.anomalyAt !== null) {
            try { listener(this.summary(), Math.round(this.anomalyAt - this.startAt)); } catch { /* ignore */ }
        }
    }

    summary(): ProbeSummary {
        return {
            active: this.active,
            nullReads: this.nullReads,
            valueChanges: this.valueChanges,
            removedBy: this.lastRemovedBy ?? 'none',
            storageEvents: this.storageEvents,
            lastNullReadSinceStartMs: this.lastNullReadAt === null ? null : Math.round(this.lastNullReadAt - this.startAt),
            nullReadKeyListed: this.firstNullRead?.keyListed ?? null,
            nullReadStorageLen: this.firstNullRead?.storageLen ?? null,
            healed: this.healed,
            last: this.events.slice(-SUMMARY_EVENTS),
        };
    }

    /** `<status> <code>` of the last failed auth backend call within HTTP_ERROR_WINDOW_MS, else `none` */
    lastAuthHttpError(now = this.now()): string {
        const error = this.lastHttpError;
        return error && now - error.at <= HTTP_ERROR_WINDOW_MS ? `${error.status} ${error.code}` : 'none';
    }

    /** the key was seen non-null within KEY_BEFORE_DROP_WINDOW_MS before `now` */
    keyBeforeDrop(now = this.now()): 'yes' | 'no' | 'unknown' {
        if (!this.active || this.shadowAt === null) return 'unknown';
        return now - this.shadowAt <= KEY_BEFORE_DROP_WINDOW_MS ? 'yes' : 'no';
    }

    /** other open tabs of the origin (seen on the BroadcastChannel), null without the channel */
    tabs(): number | null {
        return this.channel ? this.peers.size : null;
    }

    /** everything the drop report adds from the probe and the page context (task 2) - captured at the drop */
    dropContext(): AuthDropContext {
        const now = this.now();
        return {
            probe: this.summary(),
            keyBeforeDrop: this.keyBeforeDrop(now),
            lastAuthHttpError: this.lastAuthHttpError(now),
            navType: this.navigationType(),
            standalone: this.safe(() => (navigator as unknown as { standalone?: unknown }).standalone === true, false),
            inApp: this.inAppBrowser(),
            visibility: this.safe(() => document.visibilityState as string, 'unknown'),
            hiddenSinceStart: this.hiddenSinceStart,
            tabs: this.tabs(),
            storageQuotaErr: this.quotaError ? 'yes' : 'no',
        };
    }

    // ------------------------------------------------------------------ storage wrappers
    private wrapStorage(): void {
        const proto = Storage.prototype;
        const originals: StorageOriginals = { getItem: proto.getItem, setItem: proto.setItem, removeItem: proto.removeItem };
        this.originals = originals;
        const probe = this;
        const tracked = (storage: Storage, key: unknown): key is string => probe.active && storage === probe.storage && typeof key === 'string' && key.startsWith(AUTH_USER_KEY_PREFIX);
        proto.getItem = function (this: Storage, key: string): string | null {
            const value = originals.getItem.call(this, key);
            if (!tracked(this, key)) return value;
            try {
                return probe.onRead(value);
            } catch {
                probe.disable();
                return value;
            }
        };
        proto.setItem = function (this: Storage, key: string, value: string): void {
            const isTracked = tracked(this, key);
            try {
                originals.setItem.call(this, key, value);
            } catch (error) {
                if (isTracked) probe.quotaError = true;
                throw error;
            }
            if (!isTracked) return;
            try { probe.onWrite(String(value)); } catch { probe.disable(); }
        };
        proto.removeItem = function (this: Storage, key: string): void {
            originals.removeItem.call(this, key);
            if (!tracked(this, key)) return;
            try { probe.onRemove(); } catch { probe.disable(); }
        };
    }

    /** a read of the auth key: nullRead (shadow set, null read), valueChanged (another string), otherwise the shadow follows */
    private onRead(value: string | null): string | null {
        const now = this.now();
        if (value === null) {
            if (this.shadow === null) return null;
            this.nullReads++;
            this.lastNullReadAt = now;
            const frames = stackFrames();
            const listing = this.keyListing();
            if (this.firstNullRead === null) this.firstNullRead = listing;
            this.record({
                type: 'nullRead', sinceStartMs: this.since(now), visibility: this.visibility(),
                caller: classifyReader(frames), stackHash: hashText(frames),
                msSinceLastWrite: this.lastWriteAt === null ? null : Math.round(now - this.lastWriteAt),
                keyListed: listing.keyListed, storageLen: listing.storageLen,
            });
            this.fireAnomaly(now);
            if (this.heal) {
                this.healed++;
                this.record({ type: 'healed', sinceStartMs: this.since(now), visibility: this.visibility() });
                return this.shadow;
            }
            return null;
        }
        if (this.shadow !== null && value !== this.shadow) {
            const identity = identityOf(value);
            this.valueChanges++;
            this.record({
                type: 'valueChanged', sinceStartMs: this.since(now), visibility: this.visibility(),
                sameUid: identity.uid !== null && identity.uid === this.shadowUid, lenBefore: this.shadow.length, lenAfter: value.length,
                tokenChanged: identity.tokenHash !== this.shadowTokenHash,
            });
            this.setShadow(value, identity, now);
        } else if (this.shadow === null) {
            this.setShadow(value, identityOf(value), now);
        } else {
            this.shadowAt = now;
        }
        return value;
    }

    private onWrite(value: string): void {
        const now = this.now();
        this.lastWriteAt = now;
        const identity = identityOf(value);
        if (this.shadow !== null && identity.uid !== null && this.shadowUid !== null && identity.uid !== this.shadowUid) {
            this.record({ type: 'uidReplaced', sinceStartMs: this.since(now), visibility: this.visibility(), sameUid: false, lenBefore: this.shadow.length, lenAfter: value.length });
        }
        this.setShadow(value, identity, now);
    }

    private onRemove(): void {
        // the SDK removes the (absent) key at every start without a stored user (directlySetCurrentUser(null)) and
        // when it migrates between persistences - a key never seen in this document is nothing to classify
        if (this.shadow === null && this.shadowAt === null) return;
        const now = this.now();
        this.lastWriteAt = now;
        const removedBy = this.classifyRemoval(now, stackFrames());
        this.lastRemovedBy = removedBy;
        this.record({ type: 'removed', sinceStartMs: this.since(now), visibility: this.visibility(), removedBy });
        this.shadow = this.shadowUid = this.shadowTokenHash = null;
    }

    private classifyRemoval(now: number, frames: string): RemovedBy {
        if (this.appSignOutAt !== null && now - this.appSignOutAt <= APP_SIGN_OUT_WINDOW_MS) return 'sdkSignOut';
        if (this.peerSignOutAt !== null && now - this.peerSignOutAt <= APP_SIGN_OUT_WINDOW_MS) return 'peerSignOut';
        if (this.lastNullReadAt !== null && now - this.lastNullReadAt <= NULL_READ_REMOVE_WINDOW_MS) return 'sdkAfterNullRead';
        if (this.lastStorageEventNullAt !== null && now - this.lastStorageEventNullAt <= STORAGE_EVENT_REMOVE_WINDOW_MS) return 'sdkAfterStorageEvent';
        if (this.lastHttpError !== null && now - this.lastHttpError.at <= HTTP_ERROR_WINDOW_MS) return 'sdkAfterHttpError';
        if (/removeCurrentUser|directlySetCurrentUser|\b_remove\b/.test(frames)) return 'sdk';
        return frames ? 'appExplicit' : 'unknown';
    }

    /** a real storage event of another context of the origin for the auth key - the K2 signature */
    private onStorageEvent(event: StorageEvent): void {
        try {
            if (!this.active || event.storageArea !== this.storage) return;
            const now = this.now();
            // the logout of another tab of the app (announced on the tabs channel) is expected, not an anomaly
            const peerSignOut = this.peerSignOutAt !== null && now - this.peerSignOutAt <= APP_SIGN_OUT_WINDOW_MS;
            if (event.key === null) {
                // storage.clear() in another context
                if (this.shadow === null) return;
                this.storageEvents++;
                this.lastStorageEventNullAt = now;
                this.record({ type: 'storageEvent', sinceStartMs: this.since(now), visibility: this.visibility(), newValueNull: true, cleared: true, peerSignOut, url: this.pathOf(event.url) });
                this.shadow = this.shadowUid = this.shadowTokenHash = null;
                if (!peerSignOut) this.fireAnomaly(now);
                return;
            }
            if (!event.key.startsWith(AUTH_USER_KEY_PREFIX)) return;
            const oldValue = event.oldValue;
            const newValue = event.newValue;
            if (oldValue === null || newValue === oldValue) return;
            this.storageEvents++;
            if (newValue === null) {
                this.lastStorageEventNullAt = now;
                this.record({ type: 'storageEvent', sinceStartMs: this.since(now), visibility: this.visibility(), newValueNull: true, peerSignOut, url: this.pathOf(event.url) });
                this.shadow = this.shadowUid = this.shadowTokenHash = null;
                if (!peerSignOut) this.fireAnomaly(now);
            } else {
                const identity = identityOf(newValue);
                this.record({
                    type: 'storageEvent', sinceStartMs: this.since(now), visibility: this.visibility(), newValueNull: false, url: this.pathOf(event.url),
                    sameUid: identity.uid !== null && identity.uid === (this.shadowUid ?? identityOf(oldValue).uid), lenBefore: oldValue.length, lenAfter: newValue.length,
                });
                this.setShadow(newValue, identity, now);
            }
        } catch {
            this.disable();
        }
    }

    // ------------------------------------------------------------------ fetch wrapper (K3)
    private wrapFetch(): void {
        if (typeof window.fetch !== 'function') return;
        const original = window.fetch;
        this.originalFetch = original;
        const probe = this;
        window.fetch = function (this: unknown, input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
            const promise = original.call(window, input, init);
            let host: string | undefined;
            try {
                const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request)?.url ?? '';
                host = probe.active ? AUTH_HOSTS.find(candidate => url.includes(candidate)) : undefined;
            } catch { /* ignore */ }
            if (!host) return promise;
            return promise.then(
                response => { probe.noteResponse(response); return response; },
                error => { probe.noteHttpError(0, 'network'); throw error; },
            );
        } as typeof fetch;
    }

    private noteResponse(response: Response): void {
        try {
            if (response.ok) return;
            const status = response.status;
            this.noteHttpError(status, 'unknown');
            response.clone().json().then((body: { error?: { message?: unknown } }) => {
                const message = body?.error?.message;
                if (typeof message === 'string' && message) this.noteHttpError(status, message.split(/[\s:]/)[0].slice(0, 40));
            }).catch(() => { /* no json body */ });
        } catch { /* ignore */ }
    }

    private noteHttpError(status: number, code: string): void {
        this.lastHttpError = { at: this.now(), status, code };
    }

    // ------------------------------------------------------------------ tabs (K2)
    private openChannel(): void {
        const Channel = (window as unknown as { BroadcastChannel?: new (name: string) => AuthStorageProbe['channel'] }).BroadcastChannel;
        if (typeof Channel !== 'function') return;
        const channel = new Channel(TABS_CHANNEL);
        if (!channel) return;
        channel.onmessage = event => {
            try {
                const data = event?.data as { t?: string; id?: string } | null;
                if (!data || typeof data.id !== 'string' || data.id === this.tabId) return;
                if (data.t === 'hi') { this.peers.add(data.id); channel.postMessage({ t: 'hey', id: this.tabId }); }
                else if (data.t === 'hey') this.peers.add(data.id);
                else if (data.t === 'bye') this.peers.delete(data.id);
                else if (data.t === 'signOut') this.peerSignOutAt = this.now();
            } catch { /* ignore */ }
        };
        channel.postMessage({ t: 'hi', id: this.tabId });
        this.channel = channel;
    }

    // ------------------------------------------------------------------ helpers
    /** is an auth key still enumerable in the storage (key() is not wrapped), and how many keys does the storage list */
    private keyListing(): { keyListed: 'yes' | 'no' | 'unknown'; storageLen: number | null } {
        try {
            const storage = this.storage as Storage;
            const storageLen = storage.length;
            for (let index = 0; index < storageLen; index++) {
                if (storage.key(index)?.startsWith(AUTH_USER_KEY_PREFIX)) return { keyListed: 'yes', storageLen };
            }
            return { keyListed: 'no', storageLen };
        } catch {
            return { keyListed: 'unknown', storageLen: null };
        }
    }

    private setShadow(value: string, identity: { uid: string | null; tokenHash: string | null }, now: number): void {
        this.shadow = value;
        this.shadowUid = identity.uid;
        this.shadowTokenHash = identity.tokenHash;
        this.shadowAt = now;
    }

    private record(event: ProbeEvent): void {
        this.events.push(event);
        if (this.events.length > EVENT_BUFFER_SIZE) this.events.splice(0, this.events.length - EVENT_BUFFER_SIZE);
    }

    private fireAnomaly(now: number): void {
        if (this.anomalyAt !== null) return;
        this.anomalyAt = now;
        if (this.anomalyListener) {
            try { this.anomalyListener(this.summary(), this.since(now)); } catch { /* ignore */ }
        }
    }

    private disable(): void {
        this.active = false;
    }

    private listen(type: string, listener: EventListener): void {
        window.addEventListener(type, listener);
        this.listeners.push([type, listener]);
    }

    private now(): number {
        return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
    }

    private since(now: number): number {
        return Math.round(now - this.startAt);
    }

    private visibility(): string {
        return this.safe(() => document.visibilityState as string, 'unknown');
    }

    private navigationType(): string {
        return this.safe(() => {
            const entry = performance.getEntriesByType('navigation')[0] as { type?: string } | undefined;
            return entry?.type ?? 'unknown';
        }, 'unknown');
    }

    private inAppBrowser(): string {
        return this.safe(() => {
            const ua = navigator.userAgent ?? '';
            return IN_APP_BROWSERS.find(([pattern]) => pattern.test(ua))?.[1] ?? 'no';
        }, 'no');
    }

    private pathOf(url: string | null | undefined): string {
        return this.safe(() => (url ? new URL(url).pathname : ''), '');
    }

    private safe<T>(read: () => T, fallback: T): T {
        try { return read(); } catch { return fallback; }
    }
}

/** the one probe of the app: installed by main.ts, read by AuthService (drop report, anomaly) and app.module.ts (counter) */
export const authStorageProbe = new AuthStorageProbe({ heal: AUTH_STORAGE_PROBE_HEAL });

/** main.ts, before the bootstrap: installs the probe and exposes it as `window.__whooAuthStorageProbe` (dev checks, Safari inspector) */
export function installAuthStorageProbe(): void {
    authStorageProbe.install();
    try {
        (window as unknown as { __whooAuthStorageProbe?: AuthStorageProbe }).__whooAuthStorageProbe = authStorageProbe;
    } catch { /* ignore */ }
}
